import { api } from "./api";
import { userStore } from "../ducks/user";
import { v4 } from "uuid";

import {
  doStartReconnectTimer,
  endReconnectTimer,
  setOtherLoginShow,
  setWebsocketUrl,
  updateReconnectTimer,
  wsConnectionStore,
} from "../ducks/wsConnection";

import { routeIncommingMessage } from "./wsInRoutes";
import { GamePlayWs, LoginWs } from "./wsOutRoutes";
import { longPollingGetRequest } from "./longPolling";

let offlineTimeOut;

// Add shared offline detection
function setupOfflineDetection() {
  if (typeof window === "undefined") return;

  window.addEventListener("offline", (event) => {
    import("../ducks/ping").then((module) => {
      module.setOffline();
      module.setErrorData({
        networkState: "offline",
        networkStateTime: Date.now(),
      });

      offlineTimeOut = setTimeout(() => {
        startReconnectTimer();
      }, 3000);
    });
  });

  window.addEventListener("online", (event) => {
    import("../ducks/ping").then((module) => {
      module.setErrorData({
        networkState: "online",
        networkStateTime: Date.now(),
      });
      module.resetRetries();
      clearTimeout(offlineTimeOut);
      endReconnectTimer();
      onOnline();
    });
  });
}

// Initialize offline detection when module loads
if (process.browser) {
  setupOfflineDetection();
}

export let websocket = null;

export function getOrCreateSessionId() {
  let sessionId: string = sessionStorage.getItem("sessionId");
  if (!sessionId) {
    sessionId = v4();
    sessionStorage.setItem("sessionId", sessionId);
  }
  return sessionId;
}

async function loginServer() {
  const { username, isGuest, id, room } = userStore.getState();
  import("../ducks/gamePlay").then(({ gamePlayStore }) => {
    const { gameId } = gamePlayStore.getState();

    try {
      const sessionId = getOrCreateSessionId();

      const user = {
        username,
        isGuest,
        userId: id,
        room,
        gameId,
        sessionId,
      };

      LoginWs.loginServer(user);
    } catch (e) {
      setTimeout(() => {
        loginServer();
      }, 3000);
    }
  });
}

function startReconnectTimer() {
  const reconnectStart = wsConnectionStore.getState().reconnectStart;
  if (!reconnectStart) {
    doStartReconnectTimer();

    const loop = () => {
      const startTime = wsConnectionStore.getState().reconnectStart;
      if (startTime == null) {
        return;
      }
      updateReconnectTimer();
      requestAnimationFrame(loop);
    };

    loop();
  }
}

let pingInterval: any;
export let useLongPolling = false;
let postTimeout: any;
let missedPongs = 0;
const maxMissedPongs = 3;

export function initWebsocketConnect() {
  api
    .get(`/site/settings?_=${new Date().getTime()}`)
    .then((response) => {
      const { ws } = response.data;
      setWebsocketUrl(ws);
      connectToWebSocket();
    })
    .catch((error) => {
      console.log("get ws number error", error);
      setTimeout(() => {
        initWebsocketConnect();
      }, getDelay());
    });
}

//Happens when server code update happens and it switches to a new server instance
export async function checkWs(ws: number) {
  import("../ducks/gamePlay").then(({ gamePlayStore }) => {
    const { gameEnded } = gamePlayStore.getState();

    const isPlaying = !gameEnded;
    if (isPlaying) {
      const unsubscribe = gamePlayStore.subscribe(({ gameEnded }) => {
        if (gameEnded && wsConnectionStore.getState().ws !== ws) {
          setTimeout(() => {
            unsubscribe();
            setWebsocketUrl(ws);
            connectToWebSocket();
          }, 2000);
        }
      });
    }

    if (!isPlaying && wsConnectionStore.getState().ws !== ws) {
      setWebsocketUrl(ws);
      connectToWebSocket();
    }
  });
}

export function otherUserLogin() {
  disconnectWebSocket();
  setOtherLoginShow(true);
}

function disconnectWebSocket() {
  if (websocket) {
    websocket.onclose = function () {};
    websocket.close();
  }
}

function onOnline() {
  // location.reload();
  console.log("onOnline");

  if (!useLongPolling) {
    initWebsocketConnect(); // Immediately try to reconnect
  } else {
    import("../ducks/gamePlay").then(async ({ gamePlayStore }) => {
      const { gameId, turns, moves } = gamePlayStore.getState();
      await switchToLongPolling(gameId);
      GamePlayWs.sendGameSync(gameId, turns, moves);
    });
  }
}

const initialDelay = 1000; // Start with a 1-second delay
const maxDelay = 30000; // Cap the delay at 30 seconds
let retries = 0;
export function getDelay() {
  let delay = Math.min(maxDelay, initialDelay * Math.pow(2, retries));
  delay = delay / 2 + (Math.random() * delay) / 2;
  return delay;
}
export function incrRetries() {
  retries++;
}

export function connectToWebSocket() {
  disconnectWebSocket();
  useLongPolling = false;
  missedPongs = 0;

  const url = wsConnectionStore.getState().websocketUrl;

  websocket = new window.WebSocket(url);

  websocket.onerror = (error: Event) => {
    import("../ducks/ping").then((module) => {
      module.setWsError();
    });
  };

  websocket.onclose = (e: CloseEvent) => {
    console.log("websocket close");
    import("../ducks/ping").then((module) => {
      import("../ducks/gamePlay").then(({ gamePlayStore }) => {
        module.setWsClose();
        module.setWsCloseErrorMsg(e.code + " " + e.reason);
        module.setDisconnectTime();

        if (e.code == 1006 || e.code == 1001) {
          const { gameEnded, gameId } = gamePlayStore.getState();
          const isPlaying = gameEnded == false;
          module.setErrorData({
            wsCloseGameEnded: gameEnded,
            wsCloseIsPlaying: isPlaying,
            wsCloseTime: Date.now(),
            wsCloseGameId: gameId,
          });
          if (isPlaying) {
            switchToLongPolling(gameId);
            return;
          }
        }

        setTimeout(() => {
          module.setRetries();
          retries++;
          startReconnectTimer();
          initWebsocketConnect();
        }, getDelay());
      });
    });
  };
  websocket.onopen = () => {
    console.log("websocket open");
    endReconnectTimer();

    startPing();

    import("../ducks/lobby").then((module) => {
      module.clearInvites();
      module.enterLobby();
    });

    loginServer();
  };

  websocket.onmessage = async (e) => {
    if (e.data === "pong") {
      clearTimeout(postTimeout);
      return;
    }

    let data: any;
    try {
      data = JSON.parse(e.data);
    } catch (e) {
      console.log("JSON parse error", e);
      return;
    }
    routeIncommingMessage(data);
  };

  websocket.sendPing = () => {
    try {
      websocket.send("ping");
    } catch (e) {
      setTimeout(() => {
        websocket.sendPing();
      }, 1000);
    }
  };
}

function startPing() {
  clearInterval(pingInterval);

  pingInterval = setInterval(() => {
    try {
      if (websocket && websocket.readyState == WebSocket.OPEN) {
        websocket.sendPing();

        postTimeout = setTimeout(() => {
          import("../ducks/gamePlay").then(({ gamePlayStore }) => {
            const { gameEnded, gameId } = gamePlayStore.getState();
            if (gameEnded) {
              return;
            }

            missedPongs++;
            console.warn(`Missed pong (${missedPongs}/${maxMissedPongs})`);

            if (missedPongs >= maxMissedPongs) {
              console.warn("Switching to long polling due to pong timeout");
              // if (useLongPolling == false) {
              switchToLongPolling(gameId);
              // }
            }
          });
        }, 2200); // Expect pong within 2 seconds
      }
    } catch (e) {
      console.log(e);
    }
  }, 5000);
}

async function switchToLongPolling(gameId: string) {
  console.log("switchToLongPolling");
  const switchStartTime = Date.now();

  clearInterval(pingInterval);
  useLongPolling = true;
  const username = userStore.getState().username;
  const sessionId = sessionStorage.getItem("sessionId");

  // Track the switch attempt
  import("../ducks/ping").then((module) => {
    module.setErrorData({
      longPollingSwitchAttempted: true,
      longPollingSwitchStartTime: switchStartTime,
      longPollingSwitchGameId: gameId,
    });
  });

  try {
    await longPollingGetRequest(username, sessionId, gameId);
    // Track successful switch
    import("../ducks/ping").then((module) => {
      module.setErrorData({
        longPollingSwitchCompleted: true,
        longPollingSwitchEndTime: Date.now(),
      });
    });
  } catch (error) {
    // Track failed switch
    import("../ducks/ping").then((module) => {
      module.setErrorData({
        longPollingSwitchFailed: true,
        longPollingSwitchError: error.message,
        longPollingSwitchEndTime: Date.now(),
      });
    });
  }
  disconnectWebSocket();
}
